import type { PrimaryFilterId } from '../../filters/types/filters';
import { ChartFallback } from '../Chart/ChartFallback';
import { isComputedFromComposition } from '../DataResolver/composition-inputs';
import type { ResolvedData } from '../DataResolver/resolve-items';
import type { ChartType } from '../PageConfigProvider/types/chart';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import type { DimensionSource } from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { DataProvider } from './DataProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
} from './types';
import { tableToTimeseriesDataSet } from './utils/table-to-timeseries-data-set';

export type MultiMetricDataMap = Partial<
  Record<MetricValues, DataProviderSet | undefined>
>;

export type MultiMetricProviderResult = {
  byMetric: MultiMetricDataMap;
  fallback?: DataProviderSet;
};

type ResolvedMetrics = {
  resolved: ResolvedData;
  metrics: readonly MetricValues[];
};

type MultiMetricProviderProps = {
  resolvedMetrics?: ResolvedMetrics;
  primaryDimension?: PrimaryFilterId;
  dimensionSource?: DimensionSource;
  metrics: MetricValues[];
  chartType: ChartType;
  metricChartTypes?: Partial<Record<MetricValues, ChartType>>;
  secondaryDimension?: SecondaryDimensionValues;
  children: DataProviderChildren<MultiMetricProviderResult>;
};

const combineStatuses = (
  headStatus: DataProviderQueryStatus,
  tailStatus: DataProviderQueryStatus
): DataProviderQueryStatus => {
  if (
    headStatus === DataProviderQueryStatus.Error ||
    tailStatus === DataProviderQueryStatus.Error
  ) {
    return DataProviderQueryStatus.Error;
  }
  if (
    headStatus === DataProviderQueryStatus.Fetching ||
    tailStatus === DataProviderQueryStatus.Fetching
  ) {
    return DataProviderQueryStatus.Fetching;
  }
  if (
    headStatus === DataProviderQueryStatus.Success &&
    tailStatus === DataProviderQueryStatus.Success
  ) {
    return DataProviderQueryStatus.Success;
  }
  return DataProviderQueryStatus.Idle;
};

export const MultiMetricProvider = ({
  resolvedMetrics,
  primaryDimension,
  dimensionSource,
  metrics,
  chartType,
  metricChartTypes,
  secondaryDimension,
  children,
}: MultiMetricProviderProps) => {
  if (metrics.length === 0) {
    return children({
      data: { byMetric: {}, fallback: undefined },
      status: DataProviderQueryStatus.Success,
    });
  }

  return (
    <CombineProviders
      resolvedMetrics={resolvedMetrics}
      primaryDimension={primaryDimension}
      dimensionSource={dimensionSource}
      metrics={metrics}
      chartType={chartType}
      metricChartTypes={metricChartTypes}
      secondaryDimension={secondaryDimension}
    >
      {children}
    </CombineProviders>
  );
};

const MetricProvider = ({
  metric,
  resolvedMetrics,
  primaryDimension,
  dimensionSource,
  chartType,
  secondaryDimension,
  children,
}: {
  metric: MetricValues;
  resolvedMetrics?: ResolvedMetrics;
  primaryDimension?: PrimaryFilterId;
  dimensionSource?: DimensionSource;
  chartType: ChartType;
  secondaryDimension?: SecondaryDimensionValues;
  children: DataProviderChildren<DataProviderSet>;
}) => {
  const resolved = resolvedMetrics?.metrics.includes(metric)
    ? resolvedMetrics.resolved
    : undefined;
  if (resolved && !isComputedFromComposition(metric)) {
    return (
      <ChartFallback status={resolved.status}>
        {children({
          data:
            resolved.data && tableToTimeseriesDataSet(resolved.data, metric),
          status: resolved.status,
          error: resolved.error,
        })}
      </ChartFallback>
    );
  }

  return (
    <DataProvider
      primaryDimension={primaryDimension}
      dimensionSource={dimensionSource}
      chartType={chartType}
      metric={metric}
      secondaryDimension={secondaryDimension}
      suppressDownload
      doSideEffects
      resolved={resolved}
    >
      {children}
    </DataProvider>
  );
};

const CombineProviders = ({
  resolvedMetrics,
  primaryDimension,
  dimensionSource,
  metrics,
  chartType,
  metricChartTypes,
  secondaryDimension,
  children,
}: {
  resolvedMetrics?: ResolvedMetrics;
  primaryDimension?: PrimaryFilterId;
  dimensionSource?: DimensionSource;
  metrics: MetricValues[];
  chartType: ChartType;
  metricChartTypes?: Partial<Record<MetricValues, ChartType>>;
  secondaryDimension?: SecondaryDimensionValues;
  children: DataProviderChildren<MultiMetricProviderResult>;
}) => {
  const [headMetric, ...restMetrics] = metrics;

  if (headMetric === undefined) {
    return children({
      data: { byMetric: {} },
      status: DataProviderQueryStatus.Success,
    });
  }

  const effectiveChartType = metricChartTypes?.[headMetric] ?? chartType;

  if (restMetrics.length === 0) {
    return (
      <MetricProvider
        resolvedMetrics={resolvedMetrics}
        primaryDimension={primaryDimension}
        dimensionSource={dimensionSource}
        chartType={effectiveChartType}
        metric={headMetric}
        secondaryDimension={secondaryDimension}
      >
        {({ data, status }) => {
          return children({
            data: { byMetric: { [headMetric]: data } },
            status,
          });
        }}
      </MetricProvider>
    );
  }

  return (
    <MetricProvider
      resolvedMetrics={resolvedMetrics}
      primaryDimension={primaryDimension}
      chartType={effectiveChartType}
      dimensionSource={dimensionSource}
      metric={headMetric}
      secondaryDimension={secondaryDimension}
    >
      {({ data: headData, status: headStatus }) => (
        <CombineProviders
          resolvedMetrics={resolvedMetrics}
          primaryDimension={primaryDimension}
          metrics={restMetrics}
          chartType={chartType}
          metricChartTypes={metricChartTypes}
          secondaryDimension={secondaryDimension}
        >
          {({ data: tailData, status: tailStatus }) => {
            return children({
              data: {
                byMetric: {
                  [headMetric]: headData,
                  ...(tailData?.byMetric || {}),
                },
              },
              status: combineStatuses(headStatus, tailStatus),
            });
          }}
        </CombineProviders>
      )}
    </MetricProvider>
  );
};
